// ============================================================
//  AppTour.jsx
//  CODiii — Shepherd.js onboarding tour + standalone tooltips
//
//  OWNER: Paige (OQULi Inc.)
//
//  BEHAVIOUR:
//  - Tour resumes from the first incomplete milestone (PostHog-driven)
//  - Each milestone is its own mini Shepherd.Tour. If the milestone's
//    anchor lives on a page that isn't currently mounted (this app only
//    renders one `currentPage` at a time — see App.js's renderPage()),
//    the tour pulses the matching TopNav tab + shows a small "go here"
//    callout instead of trying (and failing) to attach to a missing
//    element. Once the user navigates there themselves, the real step
//    shows, anchored to the actual page content.
//  - Checklist/funnel events (tour_folder_linked, tour_research_used,
//    tour_scan_initiated, tour_settings_opened) are fired directly from
//    the real action handlers (see posthogEvents.js), NOT from this
//    tour's own buttons — so OnboardingChecklist reflects what the user
//    actually did whether or not they engage with this overlay at all.
//  - Compliance Scan is NOT part of the sequential walker — it only shows
//    reactively, the first time the user lands on the Compliance Tracker
//    page. This naturally excludes Starter tier (and anyone the page is
//    hidden from) without needing a tier prop, since they never land there.
//  - Tooltips are standalone, always-available, independent of tour state
//
//  DEPENDENCIES:
//  - shepherd.js
//  - tourContent.js  (all copy — no hardcoded strings here)
//  - AppTour.css     (Shepherd theme + .tour-nav-pulse — do not modify lightly)
//  - PostHog via posthogEvents.js (tour lifecycle) — milestone events fire
//    from real action handlers elsewhere, not from here.
//
//  PROPS:
//  - startTourRef: optional ref — call startTourRef.current() to re-launch
//  - currentPage: the app's current page key (App.js's `currentPage` state)
//  - onCreateProject: callback to open the real Create Project wizard
//
// ============================================================
//  DATA-TOUR ATTRIBUTES (already wired into the real components)
//
//  STEP 1 — Welcome
//    No anchor. Card centres on screen.
//    Primary button calls onCreateProject() to open the real wizard.
//
//  STEP 2 — Link Folder + Classify Docs  (requiredPage: 'requirements')
//    link     → <FolderInputSection data-tour="link-folder"> (RequirementsPage.jsx)
//    classify → <Modal data-tour="classify-modal"> (ClassifierModal.jsx)
//    Nav pulse target: [data-tour-nav="link-folder"] (TopNav "Link Folders" tab)
//
//  STEP 3 — Research  (requiredPage: 'dashboard')
//    Card centers on screen; pulses <DocumentsPanel data-tour="research-doc-panel">
//    (DashboardChatbot.jsx) with a glow + bouncing arrow instead of anchoring
//    the card to it (see pulseTarget). Manual "Next" button.
//    Nav pulse target: [data-tour-nav="research"] (TopNav "Research" tab)
//
//  STEP 4 — Project Settings  (requiredPage: 'project-settings')
//    Nav pulse to Project Settings shows right after Research's "Next" —
//    the real step card only appears once the user actually navigates there
//    (so they read it while looking at the real fields, not before). Card
//    centers on screen with the overlay dimming the page, same as Welcome.
//    Nav pulse target: [data-tour-nav="settings"] (TopNav.jsx, desktop + mobile)
//
//  STEP 5 — Requirements Register  (requiredPage: 'extracted-requirements')
//    Card centers on screen; pulses <SearchInput data-tour="register-search">
//    (ExtractedRequirementsPage.jsx) with a glow + bouncing arrow instead of
//    anchoring the card to it (see pulseTarget).
//    Nav pulse target: [data-tour-nav="register"] (TopNav "Requirements Register" tab)
//    No "Next" button — auto-advances once the user types in the real search bar.
//
//  STEP 6 — Compliance Scan  (requiredPage: 'compliance', Studio/Firm only)
//    Card centers on screen; pulses button[data-tour="new-scan-button"]
//    (ComplianceDashboard.jsx) with a glow + bouncing arrow instead of
//    anchoring the card to it (see pulseTarget). Ends the tour 30s after a
//    real scan completes (codiii:scan-completed window event).
//    Nav pulse target: [data-tour-nav="compliance-scan"] (TopNav "Compliance Tracker" tab)
//
//  ── TOOLTIPS ──────────────────────────────────────────────────────────────
//
//  Add data-tooltip="<id>" to the ? icon element next to each UI element.
//  The tooltip trigger should be a <button> or <span> with role="button".
//
//  notifications-bell  → ? icon next to the notifications bell (NotificationsContext / bell component)
//    data-tooltip="notifications-bell"
//
//  compliance-type     → ? icon next to the compliance type filter on Requirements Register
//    data-tooltip="compliance-type"
//    (add when the filter UI is built)
//
//  source-documents    → ? icon in the source documents panel header on Requirements Register
//    data-tooltip="source-documents"
//    (add when the panel is built)
//
//  research-docs       → ? icon in the DocumentsHeaderX on DashboardChatbot
//    <DocumentsHeaderX>
//      <h2>Select Documents</h2>
//      <button data-tooltip="research-docs" aria-label="About document selection">?</button>
//
//  scan-paths          → ? icon near scan type selector in ComplianceScanModal
//    Add inside .compliance-scan-path-section:
//    <button data-tooltip="scan-paths" aria-label="About scan types">?</button>
//
//  document-roles      → ? icon inside ClassifierModal near the role selector
//    Already suggested in the classifier modal UI — add:
//    <button data-tooltip="document-roles" aria-label="About document roles">?</button>
//
// ============================================================

import React, { useEffect, useRef, useCallback, useMemo, useState } from 'react';
import Shepherd from 'shepherd.js';
import 'shepherd.js/dist/css/shepherd.css';
import '../styles/AppTour.css';
import { tourSteps, tooltips } from './tourContent';
import { planHasCompliance } from '../utils/userAccess';
import { phTourStarted, phTourStepCompleted, phTourSkipped, phTourCompleted } from '../utils/posthogEvents';
import { fetchOnboardingProgress } from '../utils/onboardingProgress';

// ─── Milestones ───────────────────────────────────────────────────────────
// `event` is the checklist/funnel event that marks this milestone complete
// (fired elsewhere, from the real action — see posthogEvents.js). `requiredPage`
// + `navSelector` gate a milestone behind the user actually navigating to the
// right page first (pulsing the matching nav tab in the meantime); `welcome`
// is the only milestone that needs neither, since it centers on screen with
// no real-page anchor at all.
//
// The milestone sequence is TIER-DEPENDENT (see buildMilestones below): the
// Compliance Scan milestone is only included for Studio/Firm plans. Project
// Settings runs right after Research (before Register) for both tiers, so
// users have something useful to do while the background extraction runs.

const M_WELCOME = { id: 'welcome', event: null };

const M_LINK_AND_CLASSIFY = {
  id: 'linkAndClassify', event: 'tour_folder_linked',
  requiredPage: 'requirements', navSelector: '[data-tour-nav="link-folder"]',
  navPrompt: 'Head to Link Folders to continue the tour',
  // The classify sub-step anchors inside the real ClassifierModal, which the
  // user must keep interacting with (clicking the real "Begin Extraction"
  // button) — a modal overlay would block/gray that out, so this milestone
  // runs without one.
  noOverlay: true,
};

const M_RESEARCH = {
  id: 'research', event: 'tour_research_used',
  requiredPage: 'dashboard', navSelector: '[data-tour-nav="research"]',
  navPrompt: 'Head to Research to continue the tour',
  // No postCompleteDelayMs here (unlike Register/Compliance) — the nav pulse
  // to Project Settings is non-blocking, so it can show immediately after
  // "Next" and just wait for the user to get there in their own time, instead
  // of the walker sitting frozen for several minutes first.
};

// `postCompleteDelayMs` is applied per-tier in buildMilestones: 15s before the
// Compliance Scan card (Studio/Firm), or 20s before the tour ends (Starter).
// noOverlay, same reason as linkAndClassify/complianceScan: this step has no
// "Next" button and auto-advances off the real search input's `input` event —
// a modal overlay (which has no cutout to punch since this step has no
// attachTo) would block clicks/focus into that input and leave the card stuck.
const M_REGISTER_VIEWED = {
  id: 'registerViewed', event: 'tour_register_viewed',
  requiredPage: 'extracted-requirements', navSelector: '[data-tour-nav="register"]',
  navPrompt: 'Head to Requirements Register to continue the tour',
  noOverlay: true,
};

// Studio/Firm only. Lives on the Compliance Tracker page — pulses the nav tab
// until the user navigates there, then prompts them to run their first scan.
// Ends the tour 30s AFTER a real scan completes (the step listens for the
// `codiii:scan-completed` window event; postCompleteDelayMs adds the 30s) —
// this is the last milestone for Studio/Firm now that Settings runs earlier.
// noOverlay so the user can actually click "New Scan" and use the scan modal.
const M_COMPLIANCE_SCAN = {
  id: 'complianceScan', event: 'tour_scan_initiated',
  requiredPage: 'compliance', navSelector: '[data-tour-nav="compliance-scan"]',
  navPrompt: 'Head to Compliance Tracker to continue the tour',
  noOverlay: true,
  postCompleteDelayMs: 30000,
};

// Waits for the user to actually navigate to Project Settings (nav pulse +
// "Head to Project Settings..." bubble in the meantime) instead of showing
// the full explanation immediately from wherever they are — they're meant to
// read it once they're looking at the real settings fields, not before.
const M_PROJECT_SETTINGS = {
  id: 'projectSettings', event: 'tour_settings_opened',
  requiredPage: 'project-settings', navSelector: '[data-tour-nav="settings"]',
  navPrompt: 'Head to Project Settings to continue the tour',
};

/**
 * Assemble the milestone sequence for a given plan tier.
 * @param {boolean} includeCompliance — true for Studio/Firm, false for Starter.
 */
function buildMilestones(includeCompliance) {
  const register = {
    ...M_REGISTER_VIEWED,
    // Give the user a moment on the Register before the next card appears,
    // instead of advancing the instant they type their first search character.
    postCompleteDelayMs: includeCompliance ? 15000 : 20000,
  };
  return includeCompliance
    ? [M_WELCOME, M_LINK_AND_CLASSIFY, M_RESEARCH, M_PROJECT_SETTINGS, register, M_COMPLIANCE_SCAN]
    : [M_WELCOME, M_LINK_AND_CLASSIFY, M_RESEARCH, M_PROJECT_SETTINGS, register];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Which milestone events have already been completed for this user.
 *
 * Reads from localStorage (see onboardingProgress.js) so the tour resumes from
 * the first incomplete milestone instead of restarting from Welcome on every
 * refresh. This is the interim source until the PostHog Query API Lambda proxy
 * is built for cross-device persistence (TODO Sprint 9 — Shivani; see the full
 * implementation note in OnboardingChecklist.jsx).
 *
 * @param {string} userId — auth.user?.sub, passed explicitly rather than
 *   resolved from PostHog's distinct_id, which isn't reliably pointed at this
 *   user yet this early after sign-in (see onboardingProgress.js header).
 */
async function fetchCompletedMilestones(userId) {
  const { events } = await fetchOnboardingProgress(userId);
  return events; 
}

/**
 * Wait for a selector to appear in the DOM (e.g. a modal that only mounts
 * once a real async action completes), instead of showing a step anchored
 * to nothing.
 */
function waitForElement(selector, { timeout = 15000, signal } = {}) {
  return new Promise((resolve) => {
    if (signal?.aborted) { resolve(null); return; }
    const found = document.querySelector(selector);
    if (found) { resolve(found); return; }

    const start = Date.now();
    const cleanup = () => {
      observer.disconnect();
      signal?.removeEventListener('abort', onAbort);
    };
    // `signal` lets callers cancel an open-ended wait (timeout: Infinity) when
    // their tour step is torn down, so the observer doesn't leak forever.
    const onAbort = () => { cleanup(); resolve(null); };
    const observer = new MutationObserver(() => {
      const el = document.querySelector(selector);
      if (el) {
        cleanup();
        resolve(el);
      } else if (Date.now() - start > timeout) {
        cleanup();
        resolve(null); // give up quietly rather than leaving the tour stuck
      }
    });

    signal?.addEventListener('abort', onAbort, { once: true });
    // attributes: true matters here — e.g. the register-search input already
    // exists in the DOM and only gains its data-tour attribute conditionally
    // once results are populated, rather than being a newly-mounted node.
    observer.observe(document.documentElement, { childList: true, subtree: true, attributes: true });
  });
}

/**
 * Resolve once a selector disappears from the DOM (e.g. a modal that closes
 * itself after its own real confirm action succeeds). Used to advance the
 * tour off of the real action instead of a button on the tour card itself.
 */
function waitForElementRemoved(selector, { timeout = 5 * 60 * 1000, signal } = {}) {
  return new Promise((resolve) => {
    if (signal?.aborted) { resolve(); return; }
    if (!document.querySelector(selector)) { resolve(); return; }

    const start = Date.now();
    const cleanup = () => {
      observer.disconnect();
      signal?.removeEventListener('abort', onAbort);
    };
    const onAbort = () => { cleanup(); resolve(); };
    const observer = new MutationObserver(() => {
      if (!document.querySelector(selector)) {
        cleanup();
        resolve();
      } else if (Date.now() - start > timeout) {
        cleanup();
        resolve(); // give up quietly rather than leaving the tour stuck
      }
    });

    signal?.addEventListener('abort', onAbort, { once: true });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  });
}

/**
 * Resolve the first time the user types into a real input (e.g. the
 * Requirements Register search bar). Used to auto-advance a step off of
 * the real action instead of a button on the tour card itself.
 */
function waitForFirstInput(selector, { timeout = 5 * 60 * 1000 } = {}) {
  return new Promise((resolve) => {
    const el = document.querySelector(selector);
    if (!el) { resolve(); return; }

    const cleanup = () => {
      el.removeEventListener('input', onInput);
      clearTimeout(timer);
    };
    const onInput = () => { cleanup(); resolve(); };
    const timer = setTimeout(() => { cleanup(); resolve(); }, timeout);

    el.addEventListener('input', onInput);
  });
}

/**
 * Build the Shepherd step array for a single milestone's mini-tour.
 */
function buildMilestoneSteps(milestone, copy, tour, onNext, onCreateProject) {
  const { id } = milestone;

  const primaryBtn = (label) => ({
    text: label,
    classes: 'shepherd-button-primary',
    action: onNext,
  });
  const skipBtn = (label) => ({
    text: label,
    classes: 'shepherd-button-secondary',
    action: () => tour.cancel(),
  });

  const html = (title, body) => `
    <div class="tour-step-content">
      <div class="tour-text">
        <h3 class="tour-title">${title}</h3>
        <div class="tour-description">${body}</div>
      </div>
    </div>
  `;

  switch (id) {

    // ── Step 1: Welcome ──────────────────────────────────────────────────────
    // No anchor — card centres on screen.
    // Primary button opens the real Create Project wizard, then advances.
    case 'welcome':
      return [{
        id: 'welcome',
        text: html(copy.title, copy.body),
        buttons: [
          skipBtn(copy.buttons.exit),
          {
            text: copy.buttons.next,
            classes: 'shepherd-button-primary',
            action: () => { onCreateProject?.(); onNext(); },
          },
        ],
      }];

    // ── Step 2: Link Folder + Classify Docs (2 sub-states) ──────────────────
    // Fully event-driven — no timers, no auto-skip. The link sub-step has no
    // "Next" button (manually advancing it was the root of the old timing bug:
    // it jumped ahead before a folder was ever linked, leaving the classify step
    // waiting for a modal that never came). Instead the link bubble persists as a
    // reminder and waits — for as long as it takes — for the real classifier
    // modal to open, which only happens once a folder has actually been linked
    // (RequirementsPage's handleFolderUrlSubmit → setClassifierModalOpen(true)).
    // The classify sub-step then auto-advances when the user closes that modal
    // via the real "Begin Extraction". Both waits are aborted if the user exits
    // the tour, so the open-ended observers don't leak.
    case 'linkAndClassify': {
      const linkAbort = new AbortController();
      const classifyAbort = new AbortController();
      let cleanupLinkPulse = () => {};
      return [
        {
          id: 'linkAndClassify-link',
          text: html(copy.link.title, copy.link.body),
          attachTo: { element: '[data-tour="link-folder"]', on: 'bottom' },
          when: {
            show: () => {
              // Pulsing glow + bouncing arrow on the folder input, same treatment
              // as the Research/Register/Compliance steps — the milestone runs
              // without a dimming overlay, so this is what draws the eye to "paste
              // your folder link here".
              cleanupLinkPulse = pulseTarget('[data-tour="link-folder"]');
              waitForElement('[data-tour="classify-modal"]', { timeout: Infinity, signal: linkAbort.signal })
                .then((el) => {
                  if (!el) return;
                  // Tear the glow/arrow down the instant the real classifier modal
                  // mounts, rather than waiting on Shepherd's own step-destroy
                  // timing — that pulse sits at z-index 10011, above the modal's
                  // own dark backdrop (z-index 10000), so any lag here showed up
                  // as the link-folder glow floating over the classifier window
                  // (more noticeable now that modal's multi-building UI is taller).
                  cleanupLinkPulse();
                  cleanupLinkPulse = () => {};
                  if (tour.isActive()) tour.next();
                });
            },
            destroy: () => { cleanupLinkPulse(); linkAbort.abort(); },
          },
          buttons: [],
        },
        {
          id: 'linkAndClassify-classify',
          text: html(copy.classify.title, copy.classify.body),
          // 'left' was a fixed assumption about how much side margin the modal
          // leaves — multi-building projects now render a wider per-row
          // applies-to control (ClassifierModal's AppliesToBox), making the
          // modal more likely to butt up against the viewport edge. 'auto'
          // lets Popper pick whichever side actually has room instead of
          // forcing 'left' and letting the card overlap the modal.
          attachTo: { element: '[data-tour="classify-modal"]', on: 'auto' },
          // The modal is already open by the time this step shows (the link step
          // advanced on its appearance); the guard just covers it mounting a beat
          // later. No timeout — purely event-driven.
          beforeShowPromise: () =>
            waitForElement('[data-tour="classify-modal"]', { timeout: Infinity, signal: classifyAbort.signal }),
          // No "Begin Extraction" button — the real classifier modal's own button
          // is the trigger. Auto-advance once that real action closes the modal.
          when: {
            show: () => {
              waitForElementRemoved('[data-tour="classify-modal"]', { signal: classifyAbort.signal })
                .then(() => { if (tour.isActive()) tour.complete(); });
            },
            destroy: () => classifyAbort.abort(),
          },
          buttons: [],
        },
      ];
    }

    // ── Step 3: Research ─────────────────────────────────────────────────────
    // Card centers on screen instead of anchoring to the panel — same treatment
    // as the Register + Compliance Scan steps. A pulsing glow + bouncing arrow
    // on the real "Select Documents" panel (added/removed via `when` below)
    // points the user to it, and the panel pops above the dimmed page via the
    // pulse class's z-index instead of being greyed out. Keeps a "Next" button
    // since (unlike Register/Compliance) there's no single real action to
    // auto-advance off of.
    case 'research': {
      let cleanupPulse = () => {};
      return [{
        id: 'research',
        text: html(copy.title, copy.body),
        beforeShowPromise: () => waitForElement('[data-tour="research-doc-panel"]'),
        when: {
          show: () => { cleanupPulse = pulseTarget('[data-tour="research-doc-panel"]'); },
          destroy: () => cleanupPulse(),
        },
        buttons: [primaryBtn(copy.buttons.next)],
      }];
    }

    // ── Step 5: Requirements Register ──────────────────────────────────────────
    // Card centers on screen instead of anchoring to the search bar — same
    // treatment as the Compliance Scan step. A pulsing glow + arrow on the
    // real search input (added/removed via `when` below) point the user to
    // it instead. No "Next" button — auto-advances once the user starts
    // typing in the real search bar, same pattern as the classify step.
    case 'registerViewed': {
      let cleanupPulse = () => {};
      return [{
        id: 'registerViewed',
        text: html(copy.title, copy.body),
        // The real search input only carries this attribute once extraction
        // has actually populated the Register (see ExtractedRequirementsPage.jsx)
        // — extraction can take several minutes, so wait generously for it.
        beforeShowPromise: () => waitForElement('[data-tour="register-search"]', { timeout: 30 * 60 * 1000 }),
        when: {
          show: () => {
            cleanupPulse = pulseTarget('[data-tour="register-search"]');
            waitForFirstInput('[data-tour="register-search"]').then(() => {
              if (tour.isActive()) tour.complete();
            });
          },
          destroy: () => cleanupPulse(),
        },
        buttons: [],
      }];
    }

    // ── Step 6: Compliance Scan (Studio/Firm only) ───────────────────────────
    // Card centers on screen instead of anchoring to the button — the button
    // sits in the page header's top-right corner, which left too little room
    // for an anchored card and pushed it off to one side. A pulsing glow +
    // arrow on the real button (added/removed via `when` below) point the
    // user to it instead.
    //
    // No "Next" button — this step auto-advances off the REAL action, same
    // pattern as the classify + register steps: it completes once a scan
    // actually finishes (the `codiii:scan-completed` window event, dispatched
    // from ComplianceDashboard's scan-complete handler). The milestone's
    // postCompleteDelayMs then waits 30s before showing Project Settings.
    case 'complianceScan': {
      let cleanupPulse = () => {};
      let cleanupStartWatch = () => {};
      return [{
        id: 'complianceScan',
        text: html(copy.title, copy.body),
        beforeShowPromise: () => waitForElement('[data-tour="new-scan-button"]'),
        when: {
          show: () => {
            cleanupPulse = pulseTarget('[data-tour="new-scan-button"]');

            // Dismiss the tour card the moment the user clicks "New Scan", so it
            // never covers the scan modal. Advancing to Project Settings (30s
            // after the scan completes) is handled at the component level — see
            // the codiii:scan-completed effect — so it survives this card being
            // hidden AND the user navigating away from the page mid-scan.
            const scanBtn = document.querySelector('[data-tour="new-scan-button"]');
            if (scanBtn) {
              const onScanStart = () => {
                cleanupPulse();
                cleanupPulse = () => {};
                if (tour.isActive()) tour.hide();
              };
              scanBtn.addEventListener('click', onScanStart, { once: true });
              cleanupStartWatch = () => scanBtn.removeEventListener('click', onScanStart);
            }
          },
          destroy: () => {
            cleanupPulse();
            cleanupStartWatch();
          },
        },
        buttons: [],
      }];
    }

    // ── Step 4: Project Settings ─────────────────────────────────────────────
    // Shown only once the user has actually navigated to Project Settings
    // (gated by M_PROJECT_SETTINGS' requiredPage — see the nav-pulse handling
    // in the driver effect). Card centers on screen with the overlay dimming
    // the real page behind it, same treatment as Welcome.
    case 'projectSettings':
      return [{
        id: 'projectSettings',
        text: html(copy.title, copy.body),
        buttons: [
          {
            text: copy.buttons.next,
            classes: 'shepherd-button-primary',
            action: () => tour.complete(),
          },
        ],
      }];

    default:
      return [];
  }
}

// ─── Tooltip helper ───────────────────────────────────────────────────────────

/**
 * Creates and attaches a standalone Shepherd tooltip to a trigger element.
 * The tooltip is a single-step tour with no overlay.
 *
 * @param {string} attachSelector   — CSS selector for the ? icon
 * @param {object} copy             — { title, body } from tooltips export
 * @returns {function}              — cleanup function (call on unmount)
 */
function attachTooltip(attachSelector, copy) {
  const trigger = document.querySelector(attachSelector);
  if (!trigger) return () => {};

  const tooltip = new Shepherd.Tour({
    useModalOverlay: false,
    defaultStepOptions: {
      classes: 'shepherd-theme-codiii',
      scrollTo: false,
      cancelIcon: { enabled: true, label: 'Close' },
      modalOverlayOpeningPadding: 0,
      popperOptions: {
        modifiers: [{ name: 'offset', options: { offset: [0, 12] } }],
      },
    },
  });

  tooltip.addStep({
    id: `tooltip-${attachSelector.replace(/[^a-z0-9]/gi, '-')}`,
    text: `
      <div class="tour-step-content">
        <div class="tour-text">
          <h3 class="tour-title">${copy.title}</h3>
          <div class="tour-description">${copy.body}</div>
        </div>
      </div>
    `,
    attachTo: { element: attachSelector, on: 'auto' },
    buttons: [
      {
        text: 'Got it',
        classes: 'shepherd-button-primary',
        action: () => tooltip.complete(),
      },
    ],
  });

  const handleClick = (e) => {
    e.stopPropagation();
    if (tooltip.isActive()) {
      tooltip.cancel();
    } else {
      tooltip.start();
    }
  };

  const handleOutside = (e) => {
    if (!trigger.contains(e.target)) {
      if (tooltip.isActive()) tooltip.cancel();
    }
  };

  trigger.addEventListener('click', handleClick);
  document.addEventListener('mousedown', handleOutside);

  return () => {
    trigger.removeEventListener('click', handleClick);
    document.removeEventListener('mousedown', handleOutside);
    try { tooltip.cancel(); } catch (_) {}
  };
}

// ─── Nav-pulse helper ─────────────────────────────────────────────────────────

/**
 * Highlights the matching TopNav tab(s) (desktop + mobile both carry the same
 * data-tour-nav selector) with a pulsing CSS class, plus a small non-blocking
 * callout anchored to whichever match is actually visible. Used when the current
 * milestone's real anchor lives on a page that isn't mounted yet.
 *
 * Responsive-aware: below the nav's collapse breakpoint the desktop links are
 * `display:none` (offsetParent null) while the mobile links exist but sit inside
 * a collapsed, zero-height hamburger drawer (offsetParent is NOT null, so a naive
 * pick would anchor the callout to an invisible 0×0 box — Popper then dumps it in
 * the top-left corner). So we pick by real rendered size, fall back to the
 * hamburger button when the drawer is closed, and re-evaluate on resize and when
 * the drawer opens/closes so the callout follows the active target instead of
 * getting stranded.
 *
 * @returns {function} cleanup — removes the pulse class and dismisses the callout.
 */
function pulseNavTarget(selector, promptText) {
  const elements = Array.from(document.querySelectorAll(selector));
  if (!elements.length) return () => {};

  // An element is a usable anchor only if it's actually laid out and has size —
  // guards against display:none desktop links and the collapsed mobile drawer.
  const isVisible = (el) => {
    if (!el || el.offsetParent === null) return false;
    const r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0;
  };

  // Decide what the callout should point at right now: a genuinely-visible nav
  // link if there is one, otherwise the hamburger button (so the user knows to
  // open the menu). Returns null only if nothing is anchorable yet.
  const pickAnchor = () => {
    const link = elements.find(isVisible);
    if (link) return { el: link, text: promptText };
    const burger = document.querySelector('[data-tour-nav-hamburger]');
    if (isVisible(burger)) return { el: burger, text: 'Open the menu to continue the tour' };
    return null;
  };

  let tip = null;
  let anchorEl = null;
  let pulsedBurger = null;

  const teardownTip = () => {
    if (tip) { try { tip.complete(); } catch (_) {} tip = null; }
    if (pulsedBurger) { pulsedBurger.classList.remove('tour-nav-pulse'); pulsedBurger = null; }
    anchorEl = null;
  };

  const render = () => {
    const next = pickAnchor();
    if (!next) { teardownTip(); return; }            // nothing to point at yet
    if (next.el === anchorEl && tip) {               // same target — just reflow
      try { tip.getCurrentStep()?.updatePosition?.(); } catch (_) {}
      return;
    }
    teardownTip();
    anchorEl = next.el;

    // Pulse the hamburger too when it's our anchor (the real nav links are
    // hidden inside the closed drawer, so their pulse isn't visible).
    if (next.el.matches?.('[data-tour-nav-hamburger]')) {
      next.el.classList.add('tour-nav-pulse');
      pulsedBurger = next.el;
    }

    tip = new Shepherd.Tour({
      useModalOverlay: false,
      defaultStepOptions: {
        classes: 'shepherd-theme-codiii tour-nav-callout',
        scrollTo: false,
        cancelIcon: { enabled: false },
        popperOptions: {
          modifiers: [{ name: 'offset', options: { offset: [0, 8] } }],
        },
      },
    });
    tip.addStep({
      id: 'nav-callout',
      text: `<div class="tour-step-content"><p class="tour-description">${next.text}</p></div>`,
      attachTo: { element: next.el, on: 'bottom' },
    });
    try { tip.start(); } catch (_) {}
  };

  elements.forEach((el) => el.classList.add('tour-nav-pulse'));
  render();

  // Re-evaluate when the breakpoint flips (desktop ⇄ hamburger) or the drawer
  // opens/closes. rAF-debounced so a burst of mutations collapses to one reflow.
  let frame = null;
  const schedule = () => {
    if (frame) return;
    frame = requestAnimationFrame(() => { frame = null; render(); });
  };
  window.addEventListener('resize', schedule);
  const navBar = elements[0].closest('nav') || document.body;
  const observer = new MutationObserver(schedule);
  observer.observe(navBar, { attributes: true, childList: true, subtree: true });

  return () => {
    window.removeEventListener('resize', schedule);
    observer.disconnect();
    if (frame) cancelAnimationFrame(frame);
    elements.forEach((el) => el.classList.remove('tour-nav-pulse'));
    teardownTip();
  };
}

// ─── Target-arrow helper (centered-card steps) ─────────────────────────────────

/**
 * Adds a pulsing glow to a real page element and floats a small bouncing
 * arrow above it — for steps whose card is centered on screen instead of
 * anchored to that element (anchoring put the card in a cramped or awkward
 * spot for some targets, e.g. a button in a page header's top-right corner).
 *
 * @returns {function} cleanup — removes the glow class and the arrow element.
 */
function pulseTarget(selector) {
  const el = document.querySelector(selector);
  if (!el) return () => {};

  el.classList.add('tour-target-pulse');

  const arrow = document.createElement('div');
  arrow.className = 'tour-target-arrow';
  arrow.setAttribute('aria-hidden', 'true');
  arrow.innerHTML = `
    <svg width="28" height="34" viewBox="0 0 28 34" fill="none">
      <path d="M14 1v24M5 17l9 9 9-9" stroke="#e97024" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  `;
  document.body.appendChild(arrow);

  const position = () => {
    const rect = el.getBoundingClientRect();
    arrow.style.left = `${rect.left + rect.width / 2 - 14}px`;
    arrow.style.top = `${rect.top - 42}px`;
  };
  position();

  window.addEventListener('resize', position);
  window.addEventListener('scroll', position, true);

  return () => {
    el.classList.remove('tour-target-pulse');
    arrow.remove();
    window.removeEventListener('resize', position);
    window.removeEventListener('scroll', position, true);
  };
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * AppTour
 *
 * Walks the user through one milestone at a time. Renders nothing to the DOM
 * directly — all UI is managed by Shepherd (plus the nav-pulse CSS class).
 *
 * @prop {React.MutableRefObject} [startTourRef]
 *   Optional ref. On mount, set to a function that re-launches the tour from
 *   step 1. Useful for a "Restart tour" button elsewhere in the app.
 * @prop {string}   currentPage  App.js's current page key.
 * @prop {function} onCreateProject  Opens the real Create Project wizard.
 * @prop {string}   [planKey]  The company's plan tier (Company.planKey, e.g.
 *   'starter' | 'studio' | 'firm'). Studio/Firm get the Compliance Scan
 *   milestone; Starter skips it. See buildMilestones / planHasCompliance.
 * @prop {string}   [userId]  auth.user?.sub — used to key onboarding-progress
 *   localStorage reads. Passed explicitly (rather than read from PostHog) so
 *   resuming after sign-out/sign-in isn't racing ClarityUserProvider's async
 *   identify() call. See onboardingProgress.js header for the full reasoning.
 */
export default function AppTour({ startTourRef, currentPage, onCreateProject, planKey, userId }) {
  const tourRef = useRef(null);
  const cleanupRef = useRef([]);
  const lastStepIdRef = useRef(null);
  const navPulseCleanupRef = useRef(null);
  const internalCancelRef = useRef(false);
  const postCompleteTimeoutRef = useRef(null);
  // True while we're waiting out the Compliance milestone's post-scan delay,
  // so the walker doesn't re-show the compliance card during that window.
  const complianceAdvancingRef = useRef(false);
  // True while we're waiting out ANY other milestone's postCompleteDelayMs
  // (e.g. Register's 15-20s gap) — without this, navigating away mid-delay
  // would make the driver effect below think the just-completed milestone is
  // still active and re-show its nav pulse.
  const postCompleteAdvancingRef = useRef(false);
  // The milestone index to resume from when the user re-launches the tour
  // after exiting — so "Resume tour" picks up where they left off rather than
  // replaying the Welcome / Create Project step.
  const resumeIndexRef = useRef(0);

  const [milestoneIndex, setMilestoneIndex] = useState(null);

  useEffect(() => {
    // Keep the resume pointer synced to the last *active* milestone on every
    // render, not just inside the 'cancel' handler — that way it's correct
    // regardless of which path nulls milestoneIndex out (real exit, a stale
    // closure, etc.). Guarded so the explicit reset-to-0 on full completion
    // (see goToNextMilestone) isn't clobbered back by this effect.
    if (milestoneIndex !== null) resumeIndexRef.current = milestoneIndex;
  }, [milestoneIndex]);

  // Tier-dependent milestone sequence. Studio/Firm include the Compliance Scan
  // milestone after the Register; Starter ends the tour at the Register instead.
  // Project Settings runs right after Research (before the Register) for both.
  const includeCompliance = planHasCompliance(planKey);
  const milestones = useMemo(
    () => buildMilestones(includeCompliance),
    [includeCompliance]
  );

  const clearNavPulse = useCallback(() => {
    if (navPulseCleanupRef.current) {
      navPulseCleanupRef.current();
      navPulseCleanupRef.current = null;
    }
  }, []);

  const goToNextMilestone = useCallback(() => {
    setMilestoneIndex((idx) => {
      if (idx === null) return idx;
      const nextIdx = idx + 1;
      if (nextIdx >= milestones.length) {
        phTourCompleted({ tourId: 'app_tour' });
        // Fully finished — a later re-launch should start fresh from the top.
        resumeIndexRef.current = 0;
        return null;
      }
      return nextIdx;
    });
  }, [setMilestoneIndex, milestones.length]);

  const startMilestoneTour = useCallback((milestone, copy) => {
    const tour = new Shepherd.Tour({
      useModalOverlay: !milestone.noOverlay,
      defaultStepOptions: {
        classes: 'shepherd-theme-codiii',
        scrollTo: true,
        cancelIcon: { enabled: true, label: 'Close tour' },
        modalOverlayOpeningPadding: 8,
        modalOverlayOpeningRadius: 8,
        highlightClass: 'shepherd-highlight-codiii',
        popperOptions: {
          modifiers: [{ name: 'offset', options: { offset: [0, 16] } }],
        },
      },
    });

    tourRef.current = tour;

    const onNext = () => tour.next();
    const steps = buildMilestoneSteps(milestone, copy, tour, onNext, onCreateProject);
    tour.addSteps(steps);

    tour.on('show', (e) => {
      const newId = e?.step?.id;
      if (lastStepIdRef.current) {
        phTourStepCompleted({ tourId: 'app_tour', stepId: lastStepIdRef.current });
      }
      lastStepIdRef.current = newId;
    });

    tour.on('cancel', () => {
      tourRef.current = null;
      if (internalCancelRef.current) {
        // Programmatic teardown (e.g. the user navigated away mid-step) —
        // not a real exit, so don't fire phTourSkipped or stop the walker.
        internalCancelRef.current = false;
        return;
      }
      phTourSkipped({ tourId: 'app_tour', stepId: lastStepIdRef.current });
      // resumeIndexRef is already pointing at this milestone (kept in sync by
      // the resume-pointer effect above) — "Resume tour" returns here instead
      // of restarting from the top.
      setMilestoneIndex(null);
    });

    tour.on('complete', () => {
      tourRef.current = null;
      if (milestone.postCompleteDelayMs) {
        postCompleteAdvancingRef.current = true;
        postCompleteTimeoutRef.current = setTimeout(() => {
          postCompleteTimeoutRef.current = null;
          postCompleteAdvancingRef.current = false;
          goToNextMilestone();
        }, milestone.postCompleteDelayMs);
      } else {
        goToNextMilestone();
      }
    });

    tour.start();
  }, [onCreateProject, goToNextMilestone, setMilestoneIndex]);

  // ── Drive the walker: for the active milestone, either wait for the right
  //    page (pulsing the matching nav tab) or show its real Shepherd steps ──

  useEffect(() => {
    if (milestoneIndex === null) return;
    const milestone = milestones[milestoneIndex];
    if (!milestone) return;

    // After a scan completes we tear the card down and wait out the milestone's
    // postCompleteDelay before advancing — don't re-show the compliance card
    // (or its nav pulse) during that window.
    if (milestone.id === 'complianceScan' && complianceAdvancingRef.current) return;

    // Same idea for any other milestone's postCompleteDelayMs (e.g. Register's
    // 15-20s gap): milestoneIndex hasn't advanced yet, so without this guard,
    // navigating away mid-wait would make this effect think the just-completed
    // milestone is still active and re-show its nav pulse pointing the user back.
    if (postCompleteAdvancingRef.current) return;

    // Welcome's roadmap differs by tier — Starter never reaches a Compliance
    // Scan step, so its copy drops that line from the numbered list. Project
    // Settings also differs by tier — Starter has no team seats, so its copy
    // drops the "invite your team" mention entirely.
    let copy = tourSteps[milestone.id];
    if (milestone.id === 'welcome') {
      copy = { ...copy, body: includeCompliance ? copy.body : copy.bodyStarter };
    } else if (milestone.id === 'projectSettings' && !includeCompliance) {
      copy = { ...copy, title: copy.titleStarter, body: copy.bodyStarter };
    }
    if (!copy) {
      goToNextMilestone();
      return;
    }

    if (milestone.requiredPage && currentPage !== milestone.requiredPage) {
      if (tourRef.current) {
        internalCancelRef.current = true;
        try { tourRef.current.cancel(); } catch (_) { internalCancelRef.current = false; }
        tourRef.current = null;
      }
      if (!navPulseCleanupRef.current) {
        navPulseCleanupRef.current = pulseNavTarget(milestone.navSelector, milestone.navPrompt);
      }
      return;
    }

    clearNavPulse();
    if (!tourRef.current) {
      startMilestoneTour(milestone, copy);
    }
  }, [milestoneIndex, milestones, currentPage, startMilestoneTour, goToNextMilestone, clearNavPulse]);

  // ── Compliance Scan → Project Settings, driven by a real completed scan ─────
  // Lives at the component level (not inside the Shepherd step) so it survives
  // BOTH the card being hidden when the user clicks "New Scan" AND the user
  // navigating away from the Compliance Tracker while the scan runs. Fires once,
  // then waits out the milestone's postCompleteDelay (30s) before advancing.

  useEffect(() => {
    if (milestoneIndex === null) return undefined;
    const milestone = milestones[milestoneIndex];
    if (milestone?.id !== 'complianceScan') return undefined;

    const onScanCompleted = () => {
      // Tear down the compliance card — visible, hidden, or already gone if the
      // user navigated away — without registering a tour "skip".
      if (tourRef.current) {
        internalCancelRef.current = true;
        try { tourRef.current.cancel(); } catch (_) { internalCancelRef.current = false; }
        tourRef.current = null;
      }
      clearNavPulse();
      complianceAdvancingRef.current = true;
      if (postCompleteTimeoutRef.current) clearTimeout(postCompleteTimeoutRef.current);
      postCompleteTimeoutRef.current = setTimeout(() => {
        postCompleteTimeoutRef.current = null;
        goToNextMilestone();
      }, milestone.postCompleteDelayMs || 0);
    };

    window.addEventListener('codiii:scan-completed', onScanCompleted, { once: true });
    return () => window.removeEventListener('codiii:scan-completed', onScanCompleted);
  }, [milestoneIndex, milestones, clearNavPulse, goToNextMilestone]);

  // ── On mount: resume from first incomplete milestone ───────────────────────

  useEffect(() => {
    let cancelled = false;

    async function init() {
      const completed = await fetchCompletedMilestones(userId);
      if (cancelled) return;

      // Welcome has no event of its own, so it can never be marked "done" by a
      // checklist event directly — but if the user has completed ANY real
      // milestone they've clearly moved past Welcome, so treat it as done and
      // resume at the first genuinely-incomplete step rather than replaying the
      // Create Project card.
      const anyMilestoneDone = milestones.some((m) => m.event && completed.has(m.event));

      let startIdx = -1;
      for (let i = 0; i < milestones.length; i++) {
        const m = milestones[i];
        const isDone = m.event ? completed.has(m.event) : anyMilestoneDone;
        if (!isDone) { startIdx = i; break; }
      }
      if (startIdx === -1) return; // everything already done — tour stays hidden

      phTourStarted({ tourId: 'app_tour' });
      setMilestoneIndex(startIdx);
    }

    init();
    return () => { cancelled = true; };
    // userId is stable by the time AppTour mounts (App.js gates rendering until
    // auth resolves) — milestones intentionally omitted, see the original note.
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Expose a re-launch handle to the parent (the Onboarding Checklist's "Resume
  // tour" button). Resumes from the milestone the user left off on (tracked in
  // resumeIndexRef) rather than always restarting from Welcome.
  useEffect(() => {
    if (startTourRef) {
      startTourRef.current = () => {
        if (tourRef.current) { try { internalCancelRef.current = true; tourRef.current.cancel(); } catch (_) { internalCancelRef.current = false; } tourRef.current = null; }
        if (postCompleteTimeoutRef.current) { clearTimeout(postCompleteTimeoutRef.current); postCompleteTimeoutRef.current = null; }
        clearNavPulse();
        complianceAdvancingRef.current = false;
        postCompleteAdvancingRef.current = false;
        lastStepIdRef.current = null;
        const resumeAt = Math.min(resumeIndexRef.current ?? 0, milestones.length - 1);
        phTourStarted({ tourId: 'app_tour' });
        setMilestoneIndex(resumeAt);
      };
    }
  }, [startTourRef, clearNavPulse, setMilestoneIndex, milestones.length]);

  // ── Tooltips ───────────────────────────────────────────────────────────────

  useEffect(() => {
    const TOOLTIP_MAP = [
      // Bell icon — add data-tooltip="notifications-bell" to the ? trigger
      // next to the notifications bell component (wherever it lives in the app shell).
      { selector: '[data-tooltip="notifications-bell"]', copy: tooltips.notificationsBell     },

      // Requirements Register — compliance type filter ? icon
      // Add when the filter UI is built.
      { selector: '[data-tooltip="compliance-type"]',   copy: tooltips.complianceTypeFilter  },

      // Requirements Register — source documents panel ? icon
      // Panel not yet built — this selector will no-op gracefully until it is.
      // When built, add data-tooltip="source-documents" to the ? icon in the panel header.
      { selector: '[data-tooltip="source-documents"]',  copy: tooltips.sourceDocumentsPanel  },

      // DashboardChatbot — ? icon inside DocumentsHeaderX (right panel header)
      { selector: '[data-tooltip="research-docs"]',     copy: tooltips.researchDocumentPanel },

      // ComplianceScanModal — ? icon near scan path selector
      { selector: '[data-tooltip="scan-paths"]',        copy: tooltips.scanPaths             },

      // ClassifierModal — ? icon near document role selector
      { selector: '[data-tooltip="document-roles"]',    copy: tooltips.documentRoles         },
    ];

    const cleanups = TOOLTIP_MAP.map(({ selector, copy }) =>
      attachTooltip(selector, copy)
    );

    cleanupRef.current = cleanups;
    return () => cleanups.forEach((fn) => fn());
  }, []);

  // ── Cleanup on unmount ─────────────────────────────────────────────────────

  useEffect(() => {
    return () => {
      internalCancelRef.current = true;
      try { tourRef.current?.cancel(); } catch (_) {}
      if (postCompleteTimeoutRef.current) clearTimeout(postCompleteTimeoutRef.current);
      clearNavPulse();
    };
  }, [clearNavPulse]);

  return null;
}
